/**
 * SQLite recovery — UI layer.
 *
 * A message store can be several gigabytes, so the file is read by byte ranges
 * and never held whole. What IS held is the recovered rows, which is why the
 * page reports how many there are before offering to build anything from them.
 */

import { recover, toSql, toCsv } from './sqlite-recover.js';

const T = (k, p) => {
  let s = (window.__SR_I18N && window.__SR_I18N[k]) || k;
  if (p) for (const a of Object.keys(p).sort((x, y) => y.length - x.length)) {
    s = s.split(':' + a).join(p[a]);
  }
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function human(bytes) {
  const u = [T('unit_b'), T('unit_kb'), T('unit_mb'), T('unit_gb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

const state = { file: null, result: null };

function show(html) { const o = el('sr-out'); o.innerHTML = html; o.hidden = false; }
const card = (tone, title, body, extra = '') =>
  `<div class="sr-card sr-tone-${tone}"><p class="sr-title">${title}</p>
     <p class="sr-body">${body}</p>${extra}</div>`;

const readerFor = (file) => async (off, len) => {
  if (off >= file.size) return new Uint8Array(0);
  return new Uint8Array(await file.slice(off, Math.min(file.size, off + len)).arrayBuffer());
};

async function onFile(file) {
  state.file = file;
  show(`<div class="sr-working">${T('scanning')}</div>`);
  window.__stage && window.__stage('scanning');

  let res;
  try {
    res = await recover(readerFor(file), file.size, {
      onProgress: ({ page, pages }) => {
        const w = document.querySelector('.sr-working');
        if (w) w.textContent = T('scanning_n', { pct: String(Math.round(page / pages * 100)) });
      },
    });
  } catch (err) {
    show(card('bad', T('verdict_not_sqlite'), T('body_not_sqlite')));
    window.__aeDiagnostic && window.__aeDiagnostic({ issue: (err && err.code) || 'error' });
    return;
  }
  state.result = res;
  render(res, file);
}

function render(res, file) {
  const { stats, header } = res;
  const nothing = stats.rows === 0 && stats.orphanRows === 0;
  /* Three states, not two. When every row landed in the orphan pile the file
   * DID give its data back — announcing "0 rows in 0 tables" over a report that
   * lists 910 of them reads as a failure and sends the person away. */
  const orphansOnly = stats.rows === 0 && stats.orphanRows > 0;

  const rows = [
    [T('stat_size'), human(file.size)],
    [T('stat_pages'), `${header.realPages} × ${header.pageSize}${header.pageSizeGuessed ? ` (${T('guessed')})` : ''}`],
    [T('stat_tables'), String(stats.tables)],
    [T('stat_rows'), String(stats.rows)],
    stats.orphanRows ? [T('stat_orphans'), String(stats.orphanRows)] : null,
    res.brokenCells ? [T('stat_broken'), String(res.brokenCells)] : null,
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`).join('');

  /* The findings that change what the person should expect — each only shown
   * when it is actually true, so the list is never noise. */
  const notes = [];
  if (!header.magicOk) notes.push(T('note_no_magic'));
  if (header.pageSizeGuessed) notes.push(T('note_guessed_page_size'));
  if (header.truncated) notes.push(T('note_truncated', { declared: String(header.declaredPages), real: String(header.realPages) }));
  if (res.schemaSource === 'text_scan') notes.push(T('note_schema_from_text'));
  if (res.schemaSource === 'none') notes.push(T('note_no_schema'));
  if (res.brokenCells) notes.push(T('note_broken_cells', { n: String(res.brokenCells) }));
  if (stats.orphanRows) notes.push(T('note_orphans', { n: String(stats.orphanRows) }));

  const notesHtml = notes.length
    ? `<section class="sr-block"><h3>${T('notes_title')}</h3>
         <ul class="sr-notes">${notes.map((n) => `<li>${n}</li>`).join('')}</ul></section>`
    : '';

  const tableRows = [...res.byTable.entries()].map(([name, list]) => {
    const t = res.tables.find((x) => x.name === name);
    const cols = t && t.columns.length ? t.columns.join(', ') : T('columns_unknown');
    return `<tr><th scope="row"><code>${esc(name)}</code></th><td>${list.length}</td>
              <td class="sr-cols">${esc(cols)}</td>
              <td><button type="button" class="sr-mini" data-csv="${esc(name)}">CSV</button></td></tr>`;
  }).join('');

  const tablesHtml = tableRows
    ? `<section class="sr-block"><h3>${T('tables_title')}</h3>
         <table class="sr-tables"><thead><tr><th>${T('col_table')}</th><th>${T('col_rows')}</th>
           <th>${T('col_columns')}</th><th></th></tr></thead><tbody>${tableRows}</tbody></table></section>`
    : '';

  // The button lives in the top card when the orphans ARE the result, so it is
  // not offered twice.
  const orphanHtml = res.orphans.length
    ? `<section class="sr-block"><h3>${T('orphans_title', { n: String(stats.orphanRows) })}</h3>
         <p class="sr-body">${T('orphans_desc')}</p>
         ${orphansOnly ? '' : `<div class="sr-actions"><button type="button" class="sr-btn sr-btn-ghost" id="sr-orphan-csv">${T('save_orphans')}</button></div>`}
       </section>`
    : '';

  const actions = nothing ? ''
    : orphansOnly
      ? `<div class="sr-actions">
           <button type="button" class="sr-btn" id="sr-orphan-csv">${T('save_orphans')}</button>
         </div>`
      : `<div class="sr-actions">
           <button type="button" class="sr-btn" id="sr-sql">${T('save_sql')}</button>
           <button type="button" class="sr-btn sr-btn-ghost" id="sr-all-csv">${T('save_all_csv')}</button>
         </div>`;

  show(card(nothing ? 'bad' : stats.orphanRows || res.brokenCells ? 'warn' : 'ok',
      nothing ? T('verdict_nothing') : orphansOnly ? T('verdict_orphans_only') : T('verdict_recovered'),
      nothing ? T('body_nothing')
        : orphansOnly ? T('body_orphans_only', { n: String(stats.orphanRows) })
          : T('body_recovered', { rows: String(stats.rows), tables: String(stats.tables) }),
      actions)
    + `<section class="sr-block"><h3>${T('stats_title')}</h3><table class="sr-stats">${rows}</table></section>`
    + notesHtml + tablesHtml + orphanHtml);

  wire(res, file);
  window.__aeToolComplete && window.__aeToolComplete({
    rows: stats.rows, tables: stats.tables, orphans: stats.orphanRows,
    schema: res.schemaSource, magic: header.magicOk });
}

function baseName(file) {
  return String(file.name || 'database').replace(/\.[^.]+$/, '') || 'database';
}

function wire(res, file) {
  /* The unit is the RECOVERED FILE, not each download. Taking the SQL dump and
   * then a CSV of one table is one job, and billing it twice would spend a
   * daily allowance of two on a single database. The file key makes the same
   * database free for the rest of the day, however many exports it takes. */
  let charged = false;
  const billed = () => {
    if (charged || !window.__toolLimit) return { commit() { charged = true; }, abort() {} };
    const key = window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null;
    return window.__toolLimit.begin('sqlite-repair', key);
  };

  const sql = el('sr-sql');
  if (sql) sql.addEventListener('click', () => {
    const run = billed();
    if (!run) return;
    const text = toSql(res);
    window.__downloadFile(new Blob([text], { type: 'application/sql' }), `${baseName(file)}-recovered.sql`);
    run.commit();
  });

  const all = el('sr-all-csv');
  if (all) all.addEventListener('click', async () => {
    const run = billed();
    if (!run) return;
    const { buildZip } = await import('../archive/archive-repair-core.js');
    const enc = new TextEncoder();
    const items = [];
    for (const [name, rows] of res.byTable) {
      const t = res.tables.find((x) => x.name === name);
      items.push({ path: `${name}.csv`, bytes: enc.encode(toCsv(t ? t.columns : [], rows)) });
    }
    if (res.orphans.length) items.push({ path: 'orphan-rows.csv', bytes: enc.encode(orphanCsv(res)) });
    const zip = await buildZip(items);
    window.__downloadFile(new Blob([zip], { type: 'application/zip' }), `${baseName(file)}-recovered.zip`);
    run.commit();
  });

  document.querySelectorAll('[data-csv]').forEach((b) => {
    b.addEventListener('click', () => {
      const run = billed();
      if (!run) return;
      const name = b.getAttribute('data-csv');
      const t = res.tables.find((x) => x.name === name);
      const csv = toCsv(t ? t.columns : [], res.byTable.get(name) || []);
      window.__downloadFile(new Blob([csv], { type: 'text/csv' }), `${name}.csv`);
      run.commit();
    });
  });

  const orph = el('sr-orphan-csv');
  if (orph) orph.addEventListener('click', () => {
    const run = billed();
    if (!run) return;
    window.__downloadFile(new Blob([orphanCsv(res)], { type: 'text/csv' }), `${baseName(file)}-orphans.csv`);
    run.commit();
  });
}

/** Orphan rows carry their page number: it is the only identity they have. */
function orphanCsv(res) {
  const lines = [];
  let widest = 0;
  for (const p of res.orphans) {
    for (const r of p.rows) {
      const values = [p.pageNo, r.rowid, ...r.values];
      widest = Math.max(widest, values.length);
      lines.push({ values });
    }
  }
  // The columns have no names, so they get numbers — a header two cells wide
  // over rows nine cells wide is what makes a CSV open crookedly.
  const header = ['page', 'rowid'];
  for (let i = 2; i < widest; i++) header.push(`col${i - 1}`);
  return toCsv(header, lines);
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('srDrop');
  if (!zone) return;
  zone.addEventListener('tool-uploader:file', (ev) => {
    const f = ev.detail && (ev.detail.file || (ev.detail.files && ev.detail.files[0]));
    if (f) onFile(f);
  });
  // The uploader's × removed the file — the report below it describes a database
  // that is no longer loaded, so it goes with it.
  zone.addEventListener('tool-uploader:clear', () => {
    state.file = null;
    state.result = null;
    const out = el('sr-out');
    if (out) { out.innerHTML = ''; out.hidden = true; }
  });
});

export { onFile };
